import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/HubspotCompanyList.graphql';
import { Query, QueryhubspotCompanyListArgs } from '../../__generated__/globalTypes';

export const useHubspotCompanyListQuery = (options?: QueryHookOptions<Query, QueryhubspotCompanyListArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryhubspotCompanyListArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useHubspotCompanyListLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryhubspotCompanyListArgs>
): QueryResult<Query, QueryhubspotCompanyListArgs> & {
  getHubspotCompanyList: LazyQueryExecFunction<Query, QueryhubspotCompanyListArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getHubspotCompanyList, result] = useLazyQuery<Query, QueryhubspotCompanyListArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getHubspotCompanyList, ...result };
};

export const useHubspotCompanyListPromiseQuery = () => {
  const client = useApolloClient();

  const getHubspotCompanyList = useCallback(
    (options?: Omit<QueryOptions<QueryhubspotCompanyListArgs, Query>, 'query'>) =>
      client.query<Query, QueryhubspotCompanyListArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getHubspotCompanyList };
};
// Suspense Query Hooks
export const useHubspotCompanyListSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryhubspotCompanyListArgs>
) => {
  const result = useSuspenseQuery<Query, QueryhubspotCompanyListArgs>(QUERY, {
    ...options
  });

  return result;
};
